/* eslint-disable jsx-a11y/label-has-for */
import React, { useRef, useState } from "react";
import svgs, { svgType } from "../../../../assets/svgs";

import "./uploadModalDragAndDrop.scss";

import { DropdownButton, Dropdown } from "react-bootstrap";

const DocTypesDescription = {
  w9: "W-9",
  coveredadvisoronb: "Covered Advisor Onboarding Form",
  certgoodstanding: "Certificate of Good Standing",
  persstat: "Personal Statement",
  finagr: "Financing Agreement",
  affidavit: "Income Affidavit",
  bankruptcycompr: "Bankruptcy/Compromise",
  taxlienjudg: "Tax Lien/Judgements",
  crimdoc: "Criminal Documents",
  miscdoc: "Misc. Documentation",
};

// Example types, replace or extend as needed
const FILE_TYPES = [
  { value: "w9", label: DocTypesDescription.w9 },
  { value: "coveredadvisoronb", label: DocTypesDescription.coveredadvisoronb },
  { value: "certgoodstanding", label: DocTypesDescription.certgoodstanding },
  { value: "persstat", label: DocTypesDescription.persstat },
  { value: "finagr", label: DocTypesDescription.finagr },
  { value: "affidavit", label: DocTypesDescription.affidavit },
  { value: "bankruptcycompr", label: DocTypesDescription.bankruptcycompr },
  { value: "taxlienjudg", label: DocTypesDescription.taxlienjudg },
  { value: "crimdoc", label: DocTypesDescription.crimdoc },
  { value: "miscdoc", label: DocTypesDescription.miscdoc },
];

const allowedFileTypes = [
  "image/jpeg",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv",
  "application/xml",
  "text/xml",
];

const removeBtnStyle = {
  height: "100%",
  background: "#004DB2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderTopRightRadius: 8,
  borderBottomRightRadius: 8,
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  cursor: "pointer",
  padding: 0,
  minWidth: 40,
  width: 40,
};

const fileItemContainer = {
  width: "100%",
  padding: "0 0 0 16px",
  background: "#F3F4F6",
  overflow: "hidden",
  borderRadius: 8,
  outline: "1.5px #6B748E solid",
  outlineOffset: "-1.5px",
  display: "flex",
  alignItems: "center",
  gap: 0,
  justifyContent: "flex-start",
  height: 40, // Match dropdown height
  minHeight: 40,
};

const fileNameStyle = {
  flex: "1 1 0",
  color: "rgba(0, 0, 0, 0.85)",
  fontSize: 16,
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  lineHeight: "24px",
  wordWrap: "break-word",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const fileListContainer = (files, dropdownOpen = false) => ({
  marginTop: 24,
  maxHeight: 450,
  overflowY: files.length > 3 ? "auto" : "unset",
  overflowX: files.length > 3 ? "hidden" : "unset",
  width: "auto",
  minWidth: 320,
  paddingRight: files.length > 3 ? 12 : 0, // Add space between the scroll bar and the files
});

const uploadInputStyle = {
  border: "2px dashed #aaa",
  borderRadius: 8,
  padding: 32,
  textAlign: "center",
  marginBottom: 16,
  background: "#fafafa",
  cursor: "pointer",
  marginTop: 15,
};

export default function UploadFileSection(props) {
  const fileInputRef = useRef();
  // Track touched state for each file dropdown
  const [touched, setTouched] = useState([]);
  const [dropdownOpenIdx, setDropdownOpenIdx] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Use files from props, default to []
  const files = props.files || [];

  // Clear touched state and error when files are cleared from parent
  React.useEffect(() => {
    if (files.length === 0 && touched.length !== 0) {
      setTouched([]);
    }
    if (files.length === 0 && errorMsg) {
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length]);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes
  const MAX_FILES = 10;

  const handleFiles = (fileList) => {
    const newFilesArr = Array.from(fileList);
    const totalFiles = files.length + newFilesArr.length;
    if (totalFiles > MAX_FILES) {
      setErrorMsg(
        `You can upload a maximum of ${MAX_FILES} files. Please remove a file before adding another.`
      );
      return;
    }
    setErrorMsg("");
    const oversizedFiles = newFilesArr.filter(
      (file) => file.size > MAX_FILE_SIZE
    );
    if (oversizedFiles.length > 0) {
      alert(
        `File(s) too large. Maximum allowed size is 10 MB per file. The following file(s) were not added: \n` +
          oversizedFiles.map((f) => f.name).join(", ")
      );
    }
    const invalidFiles = newFilesArr.filter(
      (file) => !allowedFileTypes.includes(file.type)
    );
    if (invalidFiles.length > 0) {
      alert(
        `File(s) with invalid type.\n\nAllowed types are .jpg, .jpeg, .png, .csv, .pdf, .doc, .docx, xml. \n\nThe following file(s) were not added: \n` +
          invalidFiles.map((f) => f.name).join(", ")
      );
    }
    const newFiles = newFilesArr
      .filter(
        (file) =>
          file.size <= MAX_FILE_SIZE && allowedFileTypes.includes(file.type)
      )
      .map((file) => ({
        file,
        type: "",
        typeDesc: "Select type",
      }));
    const updatedFiles = [...files, ...newFiles];
    setTouched((prev) => [...prev, ...new Array(newFiles.length).fill(false)]);
    props.onFilesChange && props.onFilesChange(updatedFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleTypeChange = (idx, value, label) => {
    const updatedFiles = files.map((item, i) =>
      i === idx ? { ...item, type: value, typeDesc: label } : item
    );
    props.onFilesChange && props.onFilesChange(updatedFiles);
  };

  const handleRemove = (idx) => {
    const updatedFiles = files.filter((_, i) => i !== idx);
    setTouched((prev) => prev.filter((_, i) => i !== idx));
    props.onFilesChange && props.onFilesChange(updatedFiles);
  };

  const handleDropdownBlur = (idx) => {
    setTouched((prev) => {
      const arr = [...prev];
      arr[idx] = true;
      return arr;
    });
  };

  return (
    <div>
      <h3
        style={{
          color: "#000",
          fontSize: 16,
          fontWeight: "600",
          lineHeight: "24px",
          wordWrap: "break-word",
        }}
      >
        Upload files *
      </h3>
      <div
        role="presentation"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        style={uploadInputStyle}
        onClick={() => fileInputRef.current.click()}
      >
        <div>
          <div>
            <span className="uploadIconSVGWrapper">
              {svgs[svgType.uploadIcon]}
            </span>
          </div>
          <div
            style={{
              color: "rgba(0, 0, 0, 0.85)",
              fontSize: "16px",
              marginTop: "5px",
              fontWeight: "400",
              lineHeight: "24px",
              wordWrap: "break-word",
            }}
          >
            Drag and drop files here, or{" "}
            <span
              style={{
                color: "#004DB2",
                textDecoration: "underline",
                fontSize: "16px",
                marginTop: "5px",
                fontWeight: "400",
                lineHeight: "24px",
                wordWrap: "break-word",
              }}
            >
              browse
            </span>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.csv,.pdf,.doc,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/jpeg,image/png,text/csv"
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        {errorMsg && (
          <span style={{ color: "#d9534f", display: "block", marginTop: 8 }}>
            {errorMsg}
          </span>
        )}
      </div>
      <span
        style={{
          color: "#001735",
          fontSize: "14px",
          fontFamily: "Lato",
          fontWeight: "400",
          lineHeight: "20px",
          wordWrap: "break-word",
        }}
      >
        Acceptable file formats: .jpg, .jpeg, .png, .csv, .pdf, .doc, .docx,
        xml.
      </span>
      <div style={fileListContainer(files, dropdownOpenIdx !== null)}>
        {files.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 20 }}>
            <div className="row" style={{ marginTop: "10px" }}>
              <div className="col-md-12">
                <label htmlFor={`type_select_${idx}`}>
                  <strong
                    style={{
                      color: "#000",
                      fontSize: "16px",
                      fontFamily: "Lato",
                      fontWeight: "600",
                      lineHeight: "24px",
                      wordWrap: "break-word",
                    }}
                  >
                    File Type*
                  </strong>
                </label>
                <div
                  className={`dropdown-wrapper${!item.type && touched[idx] ? "-invalid" : ""}`}
                >
                  <DropdownButton
                    id={`type_select_${idx}`}
                    type="default"
                    variant="secondary"
                    title={item.typeDesc || "Select type"}
                    selected={!!item.type}
                    autoComplete="new-password"
                    disabled={props.isLoading}
                    onSelect={(key, event) => {
                      const selected = FILE_TYPES.find(
                        (opt) => opt.value === key
                      );
                      handleTypeChange(
                        idx,
                        key,
                        selected ? selected.label : "Select type"
                      );
                    }}
                    onBlur={() => handleDropdownBlur(idx)}
                    // popperConfig={{ strategy: "fixed" }}
                    onToggle={(isOpen) =>
                      setDropdownOpenIdx(isOpen ? idx : null)
                    }
                    style={
                      !item.type && touched[idx]
                        ? { borderColor: "#d9534f" }
                        : {}
                    }
                  >
                    {FILE_TYPES.map((option) => (
                      <Dropdown.Item
                        key={option.value}
                        eventKey={option.value}
                        id={`type_option_${option.value}_${idx}`}
                      >
                        {option.label}
                      </Dropdown.Item>
                    ))}
                  </DropdownButton>
                </div>
                <div
                  id={`type_select_error_${idx}`}
                  className="invalid-feedback"
                  style={{
                    display: !item.type && touched[idx] ? "block" : "none",
                    color: "#d9534f",
                  }}
                >
                  Please choose a Type
                </div>
              </div>
            </div>
            <div style={{ height: 12 }} />
            <div style={fileItemContainer}>
              <div
                style={{
                  flex: "1 1 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "flex-start",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <div style={fileNameStyle}>{item.file.name}</div>
              </div>
              <div
                style={removeBtnStyle}
                onClick={() => !props.isLoading && handleRemove(idx)}
                title="Remove"
                aria-label="Remove"
                role="presentation"
                disabled={props.isLoading}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {svgs[svgType.trash]}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
